import { useCallback, useEffect, useState } from "react";
import type { StorageFolder, StorageObject } from "../../API";
import { client } from "../../Clients";
import type { MediaScope, MediaTab } from "./types";
import { BROWSE_PAGE_SIZE, mediaTabFolder } from "./types";
import { coerceList, createBrowseStateFromResponse, extractErrorMessage } from "./utils";

export interface MediaLibraryQuery {
	/** « all » = tout le bucket (vue racine, onglet appliqué), « folder » = le dossier `path`. */
	scope: MediaScope;
	/** Dossier courant : requis quand scope === "folder". */
	path?: string;
	/** Onglet de la vue racine : traduit en filtre de dossier racine côté serveur. */
	tab?: MediaTab;
	/** Terme de recherche (titre, nom de fichier, clé) : filtré côté serveur. */
	search?: string;
	/** Filtre taxonomies : filtré côté serveur (GET /api/storage?taxonomyIds=). */
	taxonomyIds?: string[];
	page: number;
	/** Incrémenter pour forcer un rechargement (bouton Rafraîchir, après upload/suppression). */
	refreshToken?: number;
}

export interface MediaLibraryState {
	files: StorageObject[];
	/** Sous-dossiers du dossier courant (vides en portée « all » : la racine est virtuelle). */
	folders: StorageFolder[];
	breadcrumbs: StorageFolder[];
	loading: boolean;
	error: string | null;
	hasMore: boolean;
	/** Total exact quand le serveur le donne (browse), sinon null (listing plat paginé). */
	totalFiles: number | null;
}

export interface MediaLibraryData extends MediaLibraryState {
	/** Remplace un fichier mis à jour (renommage, taxonomies…) sans refetch. */
	patchFile: (updated: StorageObject) => void;
	/** Retire un fichier supprimé sans refetch. */
	dropFile: (id: string) => void;
}

const EMPTY_STATE: MediaLibraryState = {
	files: [],
	folders: [],
	breadcrumbs: [],
	loading: true,
	error: null,
	hasMore: false,
	totalFiles: null
};

/**
 * Pipeline de données UNIQUE de la Médiathèque. Deux portées, mêmes endpoints qu'avant :
 * - « all » : GET /api/storage (limite + 1 pour détecter la page suivante), avec l'onglet
 *   traduit en dossier racine, la recherche et le filtre taxonomies passés au serveur.
 * - « folder » : GET /api/storage/browse (structure + fichiers + total). Si un filtre
 *   taxonomies est actif : browse ne le supporte pas : les fichiers viennent de
 *   GET /api/storage?folder= (récursif) et browse ne fournit plus que la structure.
 */
export const useMediaLibraryData = (query: MediaLibraryQuery): MediaLibraryData => {
	const { scope, path, tab, page, refreshToken } = query;
	const search = (query.search ?? "").trim();
	const taxonomyKey = (query.taxonomyIds ?? []).filter(Boolean).join(",");

	const [state, setState] = useState<MediaLibraryState>(EMPTY_STATE);

	useEffect(() => {
		let cancelled = false;
		const searchParam = search || undefined;
		const taxonomyParam = taxonomyKey || undefined;

		const fetchAll = async (): Promise<MediaLibraryState> => {
			const response = await client.storage.apiStorageGet(
				mediaTabFolder(tab),
				BROWSE_PAGE_SIZE + 1,
				page,
				searchParam,
				taxonomyParam
			);
			// Astuce « limite + 1 » : la présence d'une page suivante sans endpoint de comptage.
			const items = coerceList<StorageObject>(response.data);
			return {
				files: items.slice(0, BROWSE_PAGE_SIZE),
				folders: [],
				breadcrumbs: [],
				loading: false,
				error: null,
				hasMore: items.length > BROWSE_PAGE_SIZE,
				totalFiles: null
			};
		};

		const fetchFolder = async (folderPath: string): Promise<MediaLibraryState> => {
			if (!taxonomyParam) {
				const response = await client.storage.apiStorageBrowseGet(folderPath, page, BROWSE_PAGE_SIZE, searchParam);
				const browse = createBrowseStateFromResponse(response?.data);
				return {
					files: browse.objects,
					folders: browse.children,
					breadcrumbs: browse.breadcrumbs,
					loading: false,
					error: null,
					hasMore: browse.hasMore,
					totalFiles: browse.totalObjects
				};
			}
			// Filtre taxonomies : browse ne donne que la structure, le listing plat donne les fichiers.
			const [browseResponse, listResponse] = await Promise.all([
				client.storage.apiStorageBrowseGet(folderPath, 1, 1, undefined),
				client.storage.apiStorageGet(folderPath, BROWSE_PAGE_SIZE + 1, page, searchParam, taxonomyParam)
			]);
			const browse = createBrowseStateFromResponse(browseResponse?.data);
			const items = coerceList<StorageObject>(listResponse.data);
			return {
				files: items.slice(0, BROWSE_PAGE_SIZE),
				folders: browse.children,
				breadcrumbs: browse.breadcrumbs,
				loading: false,
				error: null,
				hasMore: items.length > BROWSE_PAGE_SIZE,
				totalFiles: null
			};
		};

		const run = async () => {
			setState((prev) => ({ ...prev, loading: true, error: null }));
			try {
				const next = scope === "folder" && path ? await fetchFolder(path) : await fetchAll();
				if (!cancelled) setState(next);
			} catch (error) {
				if (cancelled) return;
				console.error("Échec du chargement de la médiathèque", error);
				setState({
					...EMPTY_STATE,
					loading: false,
					error:
						extractErrorMessage(error) ??
						(scope === "folder" ? "Impossible d'ouvrir ce dossier." : "Impossible de charger les médias.")
				});
			}
		};

		run();
		return () => {
			cancelled = true;
		};
	}, [scope, path, tab, search, taxonomyKey, page, refreshToken]);

	const patchFile = useCallback((updated: StorageObject) => {
		setState((prev) => ({
			...prev,
			files: prev.files.map((file) => (file.id === updated.id ? updated : file))
		}));
	}, []);

	const dropFile = useCallback((id: string) => {
		setState((prev) => ({
			...prev,
			files: prev.files.filter((file) => file.id !== id),
			totalFiles: prev.totalFiles !== null ? Math.max(0, prev.totalFiles - 1) : null
		}));
	}, []);

	return { ...state, patchFile, dropFile };
};
